import type { AdminFlowDefinition, AdminFlowScheduleProps } from "../../models/admin-flows.models";
import { previewAdminFlowSchedule } from "../../utils/admin-flow-schedule.utils";
import { ADMIN_FLOW_BUTTON, ADMIN_FLOW_INPUT, ADMIN_FLOW_PANEL } from "../../utils/admin-flows.constants";
import { useState } from "react";

// Keep scheduling intent in the same temporary draft without implying background execution or durable storage.
const AdminFlowSchedule = ({ flow, onChange }: AdminFlowScheduleProps) => {
  const [preview, setPreview] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [previewKey, setPreviewKey] = useState("");
  const scheduleKey = `${flow.schedule.cron}:${flow.schedule.timezone}`;
  const update = (patch: Partial<AdminFlowDefinition["schedule"]>) =>
    onChange({ ...flow, schedule: { ...flow.schedule, ...patch } });

  const showPreview = () => {
    setError("");

    try {
      setPreview(previewAdminFlowSchedule(flow.schedule.cron, flow.schedule.timezone));
      setPreviewKey(scheduleKey);
    } catch (reason) {
      setPreview([]);
      setError((reason as Error).message);
    }
  };

  return (
    <section data-test="admin-flow-schedule" className="grid gap-4 lg:grid-cols-2">
      <div className={`${ADMIN_FLOW_PANEL} space-y-5 p-5`}>
        <div>
          <h2 className="text-lg font-semibold">When should this flow run?</h2>
          <p className="mt-1 text-sm opacity-60">Configure a recurring schedule for {flow.name}.</p>
        </div>
        <label className="block space-y-2 text-sm">
          <span>Frequency</span>
          <select
            data-test="admin-flow-schedule-frequency"
            className={ADMIN_FLOW_INPUT}
            value={
              ["0 7 * * *", "0 7 * * 1-5", "0 7 * * 1"].includes(flow.schedule.cron) ? flow.schedule.cron : "custom"
            }
            onChange={(event) => update({ cron: event.target.value === "custom" ? "0 8 * * *" : event.target.value })}
          >
            <option value="0 7 * * *">Every day at 07:00</option>
            <option value="0 7 * * 1-5">Weekdays at 07:00</option>
            <option value="0 7 * * 1">Mondays at 07:00</option>
            <option value="custom">Custom cron</option>
          </select>
        </label>
        <label className="block space-y-2 text-sm">
          <span>Cron expression</span>
          <input
            data-test="admin-flow-schedule-cron"
            maxLength={100}
            className={ADMIN_FLOW_INPUT}
            value={flow.schedule.cron}
            onChange={(event) => update({ cron: event.target.value })}
          />
          <span className="block text-xs opacity-60">Minute · Hour · Day of month · Month · Day of week</span>
        </label>
        <label className="block space-y-2 text-sm">
          <span>Timezone</span>
          <input
            data-test="admin-flow-schedule-timezone"
            className={ADMIN_FLOW_INPUT}
            maxLength={80}
            value={flow.schedule.timezone}
            onChange={(event) => update({ timezone: event.target.value })}
            placeholder="Europe/Zurich"
          />
        </label>
        <button data-test="admin-flow-schedule-preview" className={ADMIN_FLOW_BUTTON} onClick={showPreview}>
          Preview next occurrences
        </button>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            data-test="admin-flow-schedule-configured"
            checked={flow.schedule.enabled}
            onChange={(event) => {
              try {
                previewAdminFlowSchedule(flow.schedule.cron, flow.schedule.timezone);
                update({ enabled: event.target.checked });
                setError("");
              } catch (reason) {
                setError((reason as Error).message);
              }
            }}
          />
          Keep this schedule configured
        </label>
        <p className="rounded bg-[#FFF5DE] p-3 text-sm text-[#725011]" data-test="admin-flow-schedule-state-notice">
          Configuration only. Background dispatch is not active in this state-only release. Leaving or refreshing this
          workspace clears the schedule.
        </p>
      </div>
      <div className={`${ADMIN_FLOW_PANEL} p-5`}>
        <h2 className="font-semibold">Upcoming occurrences</h2>
        <p className="mt-1 text-xs opacity-60">Preview covers the next 14 days and respects daylight saving time.</p>
        {previewKey === scheduleKey && preview.length > 0 ? (
          <ol className="mt-5 space-y-3">
            {preview.map((date, index) => (
              <li key={`${date}-${index}`} className="border-greyscale-20 border-b pb-3 text-sm">
                {date}
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-6 text-sm opacity-60">
            {previewKey === scheduleKey
              ? "No occurrences in the next 14 days."
              : "Preview the current schedule to see upcoming times."}
          </p>
        )}
        <h3 className="mt-8 font-semibold">Execution permissions</h3>
        <p className="mt-2 text-sm opacity-60">
          A schedule does not approve OGA tools or email delivery. Manual runs use the signed-in administrator's current
          permissions.
        </p>
      </div>
    </section>
  );
};

export default AdminFlowSchedule;
